import {
  Box,
  Container,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo } from "react";
import { fetchUsers } from "../features/usersSlice";

const UserCard = ({ userId }) => {
  const users = useSelector((state) => state.users.users);
  const user = users.find((u) => u._id === userId);
  if (!user) return null;

  const initials = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <Link to={`/profile/${userId}`} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          borderRadius: "12px",
          transition: "all 0.18s",
          "&:hover": { bgcolor: "rgba(212,175,55,0.08)", transform: "translateX(2px)" },
        }}
      >
        <Avatar
          src={user.profilePicture}
          sx={{
            width: 48,
            height: 48,
            border: "2px solid #E5E7EB",
            bgcolor: "#D4AF37",
            color: "#1A1A2E",
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827", lineHeight: 1.2 }}>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", mt: 0.2 }}>
            @{user.username}
          </Typography>
          {user.work && (
            <Typography
              sx={{
                fontSize: 12,
                color: "#6B7280",
                mt: 0.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.work}
            </Typography>
          )}
        </Box>
      </Box>
    </Link>
  );
};

const FollowListPage = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { users, isLoading } = useSelector((state) => state.users);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (users.length === 0) dispatch(fetchUsers());
  }, [dispatch]);

  const profileUser = useMemo(() => users.find((u) => u._id === id), [users, id]);
  const list = useMemo(() => {
    if (!profileUser) return [];
    return type === "followers" ? profileUser.followers ?? [] : profileUser.following ?? [];
  }, [profileUser, type]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#6B7280", fontSize: 16 }}>Sign in to view this page.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "calc(100vh - 65px)", gap: 2, bgcolor: "#F0F2F5" }}>
        <CircularProgress sx={{ color: "#D4AF37" }} />
        <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>Loading…</Typography>
      </Box>
    );
  }

  const title = type === "followers" ? "Followers" : "Following";

  return (
    <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", pb: 4 }}>
      <Container maxWidth="sm" sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconButton
            onClick={() => navigate(`/profile/${id}`)}
            sx={{
              bgcolor: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
              "&:hover": { bgcolor: "#F3F4F6" },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20, color: "#1A1A2E" }} />
          </IconButton>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#111827", lineHeight: 1.1 }}>
              {title}
            </Typography>
            {profileUser && (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                {profileUser.firstName} {profileUser.lastName}
              </Typography>
            )}
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
            overflow: "hidden",
          }}
        >
          {list.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>
                {type === "followers" ? "No followers yet." : "Not following anyone yet."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              {list.map((userId) => (
                <UserCard key={userId} userId={userId} />
              ))}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default FollowListPage;
